import api from './api';
import { API_ENDPOINTS } from '../config/api';

/**
 * Payment Service
 * Handles Razorpay payment integration (redirect-based flow)
 */
export const paymentService = {
  /**
   * Limit Razorpay Checkout to the online method selected on our checkout page.
   * The order itself remains a RAZORPAY order; this only controls the instrument
   * Razorpay presents to the customer.
   */
  buildPaymentMethodConfig: (method) => {
    const supportedMethods = ['upi', 'card', 'netbanking'];

    if (!supportedMethods.includes(method)) {
      return undefined;
    }

    return {
      display: {
        blocks: {
          selected_method: {
            name: 'Selected payment method',
            instruments: [{ method }]
          }
        },
        sequence: ['block.selected_method'],
        preferences: {
          show_default_blocks: false
        }
      }
    };
  },

  /**
   * Create a Razorpay payment order for an existing order
   * @param {string} orderNumber - The order number (e.g., "ORD-1734192100-E5F6G7H8")
   * @returns {Promise<Object>} Razorpay order details for checkout
   */
  createPaymentOrder: async (orderNumber) => {
    try {
      const response = await api.post(API_ENDPOINTS.PAYMENT.CREATE_ORDER, {
        order_id: orderNumber
      });

      if (response.data.success) {
        return {
          success: true,
          paymentData: {
            razorpayOrderId: response.data.payment_data.razorpay_order_id,
            amount: response.data.payment_data.amount, // In paise
            currency: response.data.payment_data.currency,
            keyId: response.data.payment_data.key_id,
            orderId: response.data.payment_data.order_id,
            orderNumber: response.data.payment_data.order_number,
            customerName: response.data.payment_data.customer_name,
            customerEmail: response.data.payment_data.customer_email,
            customerPhone: response.data.payment_data.customer_phone
          }
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to create payment order'
      };
    } catch (error) {
      console.error('Error creating payment order:', error);

      // Handle specific error cases
      if (error.response?.status === 400) {
        return {
          success: false,
          error: error.response.data?.error || 'Invalid order or payment method'
        };
      }

      if (error.response?.status === 404) {
        return {
          success: false,
          error: 'Order not found'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create payment order'
      };
    }
  },

  /**
   * Verify Razorpay payment after completion
   * @param {Object} razorpayData - Payment data from Razorpay callback
   * @param {string} razorpayData.razorpay_order_id - Razorpay order ID
   * @param {string} razorpayData.razorpay_payment_id - Razorpay payment ID
   * @param {string} razorpayData.razorpay_signature - Razorpay signature
   * @returns {Promise<Object>} Verification result with order details
   */
  verifyPayment: async (razorpayData) => {
    try {
      const response = await api.post(API_ENDPOINTS.PAYMENT.VERIFY, {
        razorpay_order_id: razorpayData.razorpay_order_id,
        razorpay_payment_id: razorpayData.razorpay_payment_id,
        razorpay_signature: razorpayData.razorpay_signature
      });

      if (response.data.pending) {
        return { success: false, pending: true, order: response.data.order };
      }
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          order: response.data.order
        };
      }

      return {
        success: false,
        error: response.data.error || 'Payment verification failed'
      };
    } catch (error) {
      console.error('Error verifying payment:', error);

      if (!error.response || error.response.status >= 500) {
        return { success: false, pending: true };
      }

      if (error.response?.status === 400) {
        return {
          success: false,
          error: error.response.data?.error || 'Invalid payment signature'
        };
      }

      if (error.response?.status === 404) {
        return {
          success: false,
          error: 'Order not found for this payment'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Payment verification failed'
      };
    }
  },

  /**
   * Ask the backend to reconcile a failure already recorded by Razorpay.
   * The backend independently fetches the payment before changing order state.
   */
  reportPaymentFailure: async (razorpayData) => {
    if (!razorpayData?.razorpay_order_id || !razorpayData?.razorpay_payment_id) {
      return { success: false, error: 'Missing Razorpay failure identifiers' };
    }

    try {
      const response = await api.post(API_ENDPOINTS.PAYMENT.FAILURE, razorpayData);
      return {
        success: Boolean(response.data?.success),
        orderStatus: response.data?.order_status
      };
    } catch (error) {
      console.error('Error reconciling payment failure:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reconcile payment failure'
      };
    }
  },

  /**
   * Get payment status for an order
   * @param {string} orderNumber - The order number
   * @returns {Promise<Object>} Payment status details
   */
  getPaymentStatus: async (orderNumber) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.PAYMENT.STATUS}/${orderNumber}/`);
      const payload = response.data || {};
      const rawStatus = payload.payment_status ?? payload.status ?? payload;
      const normalizedStatus = typeof rawStatus === 'string'
        ? rawStatus
        : rawStatus?.payment_status || rawStatus?.status || null;

      if (payload.success) {
        return {
          success: true,
          paymentStatus: normalizedStatus,
          data: payload
        };
      }

      return {
        success: false,
        error: payload.error || 'Failed to fetch payment status',
        paymentStatus: normalizedStatus,
        data: payload
      };
    } catch (error) {
      console.error('Error fetching payment status:', error);

      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch payment status'
      };
    }
  },

  /**
   * Build Razorpay checkout options for redirect flow
   * @param {Object} paymentData - Payment data from createPaymentOrder
   * @param {string} callbackUrl - URL to redirect after payment
   * @returns {Object} Razorpay checkout options
   */
  buildCheckoutOptions: (paymentData, callbackUrl) => {
    return {
      key: paymentData.keyId,
      amount: paymentData.amount,
      currency: paymentData.currency,
      name: 'Sandhu Scientific',
      description: `Order #${paymentData.orderNumber}`,
      order_id: paymentData.razorpayOrderId,
      callback_url: callbackUrl,
      prefill: {
        name: paymentData.customerName,
        email: paymentData.customerEmail,
        contact: paymentData.customerPhone
      },
      notes: {
        order_number: paymentData.orderNumber
      },
      theme: {
        color: '#036c5f' // Primary brand color
      }
    };
  },

  /**
   * Store payment info in localStorage for callback handling
   * @param {string} orderNumber - Order number
   * @param {string} razorpayOrderId - Razorpay order ID
   */
  storePaymentSession: (orderNumber, razorpayOrderId) => {
    localStorage.setItem('pending_payment', JSON.stringify({
      orderNumber,
      razorpayOrderId,
      timestamp: Date.now()
    }));
  },

  /**
   * Get stored payment session
   * @returns {Object|null} Stored payment data or null
   */
  getPaymentSession: () => {
    const data = localStorage.getItem('pending_payment');
    if (data) {
      try {
        const parsed = JSON.parse(data);
        // Check if session is less than 1 hour old
        if (Date.now() - parsed.timestamp < 3600000) {
          return parsed;
        }
        // Clear expired session
        localStorage.removeItem('pending_payment');
      } catch (e) {
        localStorage.removeItem('pending_payment');
      }
    }
    return null;
  },

  /**
   * Clear stored payment session
   */
  clearPaymentSession: () => {
    localStorage.removeItem('pending_payment');
  },

  /**
   * Format amount from paise to rupees
   * @param {number} amountInPaise - Amount in paise
   * @returns {string} Formatted amount in rupees
   */
  formatAmount: (amountInPaise) => {
    const rupees = amountInPaise / 100;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(rupees);
  }
};

export default paymentService;
